// Carnet de l'équipement et du site : « qui est venu, quand, ce qu'il a fait et comment », la plus récente en haut.
// Téléphone d'abord : une ligne = date, quoi, qui (avec « Appeler » si le serveur l'autorise), comment, lien.
// Le serveur décide seul de la présence du téléphone (compte actif, lecteur ayant accès au site) : l'interface ne fait qu'afficher.
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Archive, CloudOff, FilePlus2, History, Phone, Replace, Stethoscope, Wrench } from 'lucide-react';
import { api, type ErreurApi } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { lienTelephone } from '@shared/telephone.ts';
import { Badge, Bouton, Chargement, EtatErreur, LienBouton, Panneau, type Ton } from './ui.tsx';
import { STATUTS_INTERVENTION } from './statuts.tsx';
import '../styles/carnet.css';

export type FamilleCarnet = 'pose' | 'depannage' | 'fiche';
export interface PersonneCarnet { id: string; nom: string; actif: boolean; telephone?: string }
export interface EvenementCarnet {
  id: string;
  type: 'creation' | 'installation' | 'depannage' | 'archivage' | 'remplacement' | 'pose' | 'etape' | 'reserve';
  famille: FamilleCarnet;
  date: string;
  jour_seulement: boolean;
  equipement: { id: string; libelle: string; archive: boolean } | null;
  qui: PersonneCarnet[];
  quoi: string;
  reference: string | null;
  statut: { code: string; libelle: string } | null;
  comment: { causes: string[]; operations: string[]; mesures: { point: string; valeur: string; unite: string }[]; texte: string | null; nb_elements: number | null };
  lien: { libelle: string; to: string } | null;
}
interface PageCarnet { evenements: EvenementCarnet[]; suite: boolean; site: { id: string; nom: string; demo: boolean } }

const fmtJour = new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Paris' });
const fmtHeure = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' });

const TYPES: Record<EvenementCarnet['type'], { libelle: string; icone: ReactNode }> = {
  depannage: { libelle: 'Dépannage', icone: <Stethoscope aria-hidden /> },
  installation: { libelle: 'Mise en service', icone: <Wrench aria-hidden /> },
  pose: { libelle: 'Pose', icone: <Wrench aria-hidden /> },
  etape: { libelle: 'Étape de chantier', icone: <Wrench aria-hidden /> },
  reserve: { libelle: 'Réserve', icone: <History aria-hidden /> },
  creation: { libelle: 'Fiche créée', icone: <FilePlus2 aria-hidden /> },
  archivage: { libelle: 'Archivage', icone: <Archive aria-hidden /> },
  remplacement: { libelle: 'Remplacement', icone: <Replace aria-hidden /> },
};

const horsConnexion = (q: { fetchStatus: string; error: unknown }) => q.fetchStatus === 'paused' || (q.error as ErreurApi | null)?.code === 'hors_connexion';

/** Nom + « Appeler » (téléphone professionnel) ou « N'est plus dans l'entreprise ». */
export function Personne({ p, avec }: { p: PersonneCarnet; avec?: boolean }) {
  const { moi } = useSession();
  const moiMeme = p.id === moi.utilisateur.id;
  return (
    <li className="carnet-personne">
      <span className="carnet-nom">{avec && <span className="discret">avec </span>}<strong>{p.nom}</strong>{moiMeme && <span className="discret"> (vous)</span>}</span>
      {!p.actif && <span className="tres-petit discret">N’est plus dans l’entreprise</span>}
      {p.actif && p.telephone && !moiMeme && (
        <span className="carnet-appel">
          <a className="btn carnet-appeler" href={lienTelephone(p.telephone)} aria-label={`Appeler ${p.nom} (téléphone professionnel ${p.telephone})`}>
            <Phone aria-hidden />Appeler</a>
          <span className="tres-petit discret mono carnet-numero">{p.telephone}</span>
        </span>
      )}
    </li>
  );
}

function LigneCarnet({ e, avecEquipement }: { e: EvenementCarnet; avecEquipement: boolean }) {
  const t = TYPES[e.type];
  const ton: Ton = e.statut ? (STATUTS_INTERVENTION[e.statut.code]?.ton ?? 'neutre') : 'neutre';
  const c = e.comment;
  return (
    <li className={`carnet-ligne carnet-${e.famille}`}>
      <div className="carnet-quand">
        <time dateTime={e.date}>{fmtJour.format(new Date(e.date))}</time>
        {!e.jour_seulement && <span className="tres-petit discret">{fmtHeure.format(new Date(e.date)).replace(':', ' h ')}</span>}
      </div>
      <div className="carnet-corps">
        <div className="carnet-entete">
          <span className="carnet-type">{t.icone}{t.libelle}</span>
          {e.reference && <span className="mono petit">{e.reference}</span>}
          {e.statut && <Badge ton={ton}>{e.statut.libelle}</Badge>}
        </div>
        <p className="carnet-quoi">{e.quoi}</p>
        {avecEquipement && e.equipement && (
          <p className="petit"><Link to={`/sites/equipements/${e.equipement.id}`}>{e.equipement.libelle}</Link>
            {e.equipement.archive && <span className="discret"> (archivé)</span>}</p>
        )}
        {(c.causes.length > 0 || c.operations.length > 0 || c.mesures.length > 0 || c.texte) && (
          <div className="carnet-comment petit">
            {c.causes.length > 0 && <p><span className="secondaire">Cause confirmée : </span>{c.causes.join(' ; ')}</p>}
            {c.operations.length > 0 && <p><span className="secondaire">Fait : </span>{c.operations.join(' ; ')}</p>}
            {c.mesures.length > 0 && (
              <p className="expert-seulement"><span className="secondaire">Mesures : </span>
                {c.mesures.map((x, k) => <span key={k}>{k ? ' ; ' : ''}{x.point} <span className="mono">{x.valeur} {x.unite}</span></span>)}</p>
            )}
            {c.texte && <p className="carnet-texte">{c.texte}</p>}
          </div>
        )}
        {e.qui.length > 0
          ? <ul className="carnet-qui" aria-label="Intervenants">{e.qui.map((p, k) => <Personne key={p.id} p={p} avec={k > 0} />)}</ul>
          : e.type === 'installation' ? null : <p className="tres-petit discret">Auteur non enregistré.</p>}
        {e.lien && <div><LienBouton compact variante="fantome" to={e.lien.to}>{e.lien.libelle}</LienBouton></div>}
      </div>
    </li>
  );
}

export function ListeCarnet({ evenements, avecEquipement = false }: { evenements: EvenementCarnet[]; avecEquipement?: boolean }) {
  return <ol className="carnet">{evenements.map((e) => <LigneCarnet key={e.id} e={e} avecEquipement={avecEquipement} />)}</ol>;
}

const FILTRES: { id: FamilleCarnet | 'tout'; libelle: string }[] = [
  { id: 'tout', libelle: 'Tout' }, { id: 'pose', libelle: 'Pose' }, { id: 'depannage', libelle: 'Dépannages' },
];

function FiltreFamille({ valeur, changer }: { valeur: FamilleCarnet | 'tout'; changer: (v: FamilleCarnet | 'tout') => void }) {
  return (
    <div className="segment carnet-filtre" role="group" aria-label="Filtrer le carnet">
      {FILTRES.map((f) => <button key={f.id} type="button" aria-pressed={valeur === f.id} onClick={() => changer(f.id)}>{f.libelle}</button>)}
    </div>
  );
}

/** Pages successives d'un carnet (« Plus ancien » charge la suite) ; la 1re page garde l'adresse simple, gardée hors connexion. */
function useCarnet(base: string, params: Record<string, string>) {
  return useInfiniteQuery({
    queryKey: ['carnet', base, params],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams(Object.entries(params).filter(([, v]) => v));
      if (pageParam) p.set('avant', pageParam);
      const qs = p.toString();
      return api.get<PageCarnet>(qs ? `${base}?${qs}` : base);
    },
    getNextPageParam: (derniere) => (derniere.suite ? derniere.evenements.at(-1)?.date ?? null : null),
    retry: (n, e) => (e as ErreurApi)?.code !== 'hors_connexion' && n < 2,
  });
}

function CorpsCarnet({ q, vide, avecEquipement }: { q: ReturnType<typeof useCarnet>; vide: string; avecEquipement?: boolean }) {
  const evenements = q.data?.pages.flatMap((p) => p.evenements) ?? [];
  if (q.isLoading && !horsConnexion(q)) return <Chargement lignes={3} libelle="Chargement du carnet…" />;
  if (horsConnexion(q) && !q.data) return <p className="petit secondaire carnet-ligne-simple"><CloudOff aria-hidden size={18} />Le carnet exige le réseau, sauf si la fiche est gardée hors connexion.</p>;
  if (q.isError && !q.data) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  if (!evenements.length) return <p className="petit secondaire">{vide}</p>;
  return (
    <div className="pile" style={{ gap: 'var(--e-3)' }}>
      <ListeCarnet evenements={evenements} avecEquipement={avecEquipement} />
      {q.hasNextPage && (
        <div><Bouton variante="fantome" chargement={q.isFetchingNextPage} onClick={() => q.fetchNextPage()}>Plus ancien</Bouton></div>
      )}
    </div>
  );
}

/** Fiche équipement : remplace l'ancien bloc « Historique des interventions ». */
export function CarnetEquipement({ equipementId }: { equipementId: string }) {
  const [famille, setFamille] = useState<FamilleCarnet | 'tout'>('tout');
  const q = useCarnet(`/api/equipements/${equipementId}/carnet`, famille === 'tout' ? {} : { famille });
  return (
    <Panneau titre="Carnet de l’équipement" id="carnet" className="carnet-panneau" actions={<FiltreFamille valeur={famille} changer={setFamille} />}>
      <CorpsCarnet q={q} vide={famille === 'tout' ? 'Rien n’est encore enregistré pour cet équipement.' : 'Aucun événement de ce type.'} />
    </Panneau>
  );
}

export interface FiltresSite {
  equipements: { id: string; label: string; archive?: boolean }[];
  lieux: { id: string; libelle: string }[];
  systemes: { id: string; name: string }[];
}

/** Fiche site, onglet « Carnet du site » : qui est venu sur ce site, quand, pour quoi faire. */
export function CarnetSite({ siteId, filtres }: { siteId: string; filtres: FiltresSite }) {
  const [famille, setFamille] = useState<FamilleCarnet | 'tout'>('tout');
  const [cible, setCible] = useState('');
  const [type, valeur] = cible ? cible.split(':') : ['', ''];
  const params: Record<string, string> = { ...(famille === 'tout' ? {} : { famille }), ...(type ? { [type]: valeur } : {}) };
  const q = useCarnet(`/api/sites/${siteId}/carnet`, params);
  return (
    <Panneau titre="Carnet du site" id="carnet-site" className="carnet-panneau"
      actions={<FiltreFamille valeur={famille} changer={setFamille} />}>
      <div className="pile" style={{ gap: 'var(--e-4)' }}>
        <div className="champ carnet-choix">
          <label className="champ-libelle" htmlFor="carnet-cible">Afficher</label>
          <select id="carnet-cible" className="saisie" value={cible} onChange={(e) => setCible(e.target.value)}>
            <option value="">Tout le site</option>
            {filtres.lieux.length > 0 && <optgroup label="Emplacement">{filtres.lieux.map((l) => <option key={l.id} value={`lieu:${l.id}`}>{l.libelle}</option>)}</optgroup>}
            {filtres.systemes.length > 0 && <optgroup label="Système">{filtres.systemes.map((s) => <option key={s.id} value={`systeme:${s.id}`}>{s.name}</option>)}</optgroup>}
            {filtres.equipements.length > 0 && (
              <optgroup label="Équipement">{filtres.equipements.map((e) => <option key={e.id} value={`equipement:${e.id}`}>{e.label}{e.archive ? ' (archivé)' : ''}</option>)}</optgroup>
            )}
          </select>
        </div>
        <CorpsCarnet q={q} avecEquipement vide={cible || famille !== 'tout' ? 'Aucun événement pour ce filtre.' : 'Rien n’est encore enregistré sur ce site.'} />
      </div>
    </Panneau>
  );
}

/** Espace de diagnostic : les 5 dernières lignes du carnet de l'équipement (hors diagnostic en cours) + « Tout le carnet ». */
export function PanneauHistoriqueEquipement({ equipementId, interventionId }: { equipementId: string; interventionId: string }) {
  const q = useQuery({
    queryKey: ['carnet-diagnostic', equipementId, interventionId],
    queryFn: () => api.get<PageCarnet>(`/api/equipements/${equipementId}/carnet?limite=5&exclure=${interventionId}`),
    retry: (n, e) => (e as ErreurApi)?.code !== 'hors_connexion' && n < 2,
  });
  const ev = q.data?.evenements ?? [];
  if (horsConnexion(q) || (q.data && !ev.length)) {
    return (
      <section className="panneau int-dejavu-discret int-o-historique" id="historique-equipement" aria-label="Historique de cet équipement">
        <p className="petit secondaire int-dejavu-ligne">{horsConnexion(q) ? <CloudOff aria-hidden size={18} /> : <History aria-hidden size={18} />}
          <span><strong>Historique de cet équipement</strong> — {horsConnexion(q) ? 'disponible au retour du réseau.' : 'rien d’autre n’est enregistré.'}</span></p>
      </section>
    );
  }
  return (
    <Panneau titre="Historique de cet équipement" id="historique-equipement" className="int-o-historique carnet-panneau"
      actions={<LienBouton compact variante="fantome" to={`/sites/equipements/${equipementId}#carnet`}>Tout le carnet</LienBouton>}>
      {q.isLoading && <Chargement lignes={2} libelle="Chargement de l’historique…" />}
      {q.isError && !horsConnexion(q) && <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />}
      {ev.length > 0 && <ListeCarnet evenements={ev} />}
    </Panneau>
  );
}
